// @dynamic-i18n-keys
// Dynamic t() at lines 28-29, 31, 48, 59: modalType is 'error' | 'action' | 'welcome'; actionType is variable.
//   verification.title.error
//   verification.title.action
//   verification.title.welcome
//   verification.tagline.error
//   verification.tagline.action
//   verification.tagline.welcome
//   verification.checkEmail.error
//   verification.checkEmail.action
//   verification.checkEmail.welcome
//   verification.instructions.error
//   verification.instructions.action
//   verification.instructions.welcome
// verification.action.*
import React from 'react';
import c from 'classnames';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { Url, URLS } from '@wix/da-url';
import { getIsUnverified } from '../../publicSession/selectors';
import { ArtBrandedDesktopPopup } from '../../ArtBrandedPopup';
import Button from '../../Button';
import VerificationEmailInput from '../../VerificationEmailInput';
import { Props } from '../VerificationPopup';
import s from './VerificationPopupDesktop.scss';

export const VerificationPopupDesktop: React.FC<
  Omit<Props, 'dropFirstTimeFlag' | 'dropFlagOnClose'>
> = ({ actionType, email, resendEmail, onClose, ...props }) => {
  const { t } = useTranslation();

  let modalType;
  if (actionType === 'error') {
    modalType = 'error';
  } else if (actionType) {
    modalType = 'action';
  } else {
    modalType = 'welcome';
  }

  const isUnverified = useSelector(getIsUnverified);
  const title = t(`verification.title.${modalType}`, {
    action: t(`verification.action.${actionType}`),
  });
  const tagline = t(`verification.tagline.${modalType}`);

  return (
    <ArtBrandedDesktopPopup
      artImageSrc={Url.staticCDNLink('eclipse/sisu/image-11-620625097.jpg')}
      artAuthorUsername={'AxiomDesign'}
      title={title}
      tagline={tagline}
      className={s[`${modalType}-modal`]}
      titleClassName={s['title']}
      taglineClassName={s['tagline']}
      contentClassName={s['content']}
      popupProps={{ isOpen: true }}
      onClose={onClose}
      {...props}
    >
      <div className={s['text']}>
        {t(`verification.checkEmail.${modalType}`, {
          postProcess: ['reactPostprocessor', 'featureBrancher'],
          email: <span className={s['email']}>{email}</span>,
        })}
      </div>
      <div className={s['text']}>
        {modalType === 'error' ? (
          <Trans i18nKey="verification.instructions.error">
            If the issue persists, try opening the verification link from a
            different device, and if that doesn&apos;t resolve the issue, please
            <a className="text-link-level-1-brand" href={URLS.SUPPORT}>
              contact DeviantArt Customer Support
            </a>
            .
          </Trans>
        ) : (
          t(`verification.instructions.${modalType}`)
        )}
      </div>
      <div className={s['buttons']}>
        <Button
          view="b3"
          height="medium"
          className={s['button']}
          onClick={() => {
            resendEmail();
            onClose && onClose();
          }}
        >
          {t('verification.resend')}
        </Button>
        {!isUnverified && (
          <div
            className={c(
              s['change-email'],
              modalType === 'error' && s['verify-error']
            )}
          >
            {t('verification.notEmail', {
              postProcess: ['reactPostprocessor', 'featureBrancher'],
              email: <span className={s['email']}>{email}</span>,
            })}
            <VerificationEmailInput className={s['change-email-input']} />
          </div>
        )}
      </div>
      {isUnverified && (
        <div className={s['footer']}>
          <Trans i18nKey="verification.doormatBlurb">
            If you don&apos;t recognize this email or no longer have access to
            it, please contact support at
            <a
              target="_blank"
              rel="noreferrer"
              href="mailto:accountrecovery@deviantart.com"
            >
              accountrecovery@deviantart.com
            </a>{' '}
            or
            <a
              target="_blank"
              rel="noreferrer"
              href="https://www.deviantartsupport.com/en/article/what-should-i-do-if-ive-lost-access-to-my-account"
            >
              learn more here
            </a>
            .
          </Trans>
        </div>
      )}
    </ArtBrandedDesktopPopup>
  );
};
VerificationPopupDesktop.displayName = 'VerificationPopupDesktop';

export default VerificationPopupDesktop;
